// src/pages/TestResults.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/config';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import QuestionRenderer from '../components/QuestionRenderer';
import '../styles/TestResults.css';

const LEARN_APP_URL = 'https://learn.litvill.com';

const LEVEL_NAMES = {
  'level-1': 'Level 1 – Roots',
  'level-2': 'Level 2 – Branches',
  'level-3': 'Level 3 – Skies',
  'level-4': 'Level 4 – Rivers',
};

// Maps each placement level to the next one in sequence
const NEXT_LEVEL = {
  'level-1': 'level-2',
  'level-2': 'level-3',
  'level-3': 'level-4',
};

const TestResults = () => {
  const { attemptId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [attempt, setAttempt] = useState(null);
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllAnswers, setShowAllAnswers] = useState(false);
  const [levelComplete, setLevelComplete] = useState(false);

  useEffect(() => {
    loadResults();
  }, [attemptId]);

  const loadResults = async () => {
    try {
      const attemptDoc = await getDoc(doc(db, 'testAttempts', attemptId));
      if (!attemptDoc.exists()) {
        alert('Results not found');
        navigate('/learner');
        return;
      }

      const attemptData = { id: attemptDoc.id, ...attemptDoc.data() };
      setAttempt(attemptData);

      // Load from correct collection depending on test type
      const collectionName = attemptData.testType === 'lessonTest' ? 'lessonTests' : 'tests';
      const testDoc = await getDoc(doc(db, collectionName, attemptData.testId));
      if (testDoc.exists()) {
        setTest({ id: testDoc.id, ...testDoc.data() });
      }

      // Check if all tests in this level are now passed
      if (attemptData.testType !== 'lessonTest' && testDoc.exists()) {
        const levelId = attemptData.levelId || testDoc.data().levelId;
        if (levelId) {
          // Get all tests for this level
          const levelTestsSnap = await getDocs(
            query(collection(db, 'tests'),
              where('levelId', '==', levelId),
              where('isActive', '==', true)
            )
          );
          const levelTestIds = levelTestsSnap.docs
            .filter(d => d.data().mode === 'placement' && !d.data().lessonCode)
            .map(d => d.id);

          // Get all passing attempts by this user for this level's tests
          const passingSnap = await getDocs(
            query(collection(db, 'testAttempts'),
              where('userId', '==', attemptData.userId),
              where('passed', '==', true)
            )
          );
          const passedTestIds = new Set(
            passingSnap.docs.map(d => d.data().testId)
          );

          const allPassed = levelTestIds.length > 0 &&
            levelTestIds.every(id => passedTestIds.has(id));
          setLevelComplete(allPassed);
        }
      }

      // Load questions
      const questionIds = attemptData.selectedQuestions || [];
      const questionDocs = await Promise.all(
        questionIds.map(id => getDoc(doc(db, 'testQuestions', id)))
      );
      setQuestions(
        questionDocs.filter(d => d.exists()).map(d => ({ id: d.id, ...d.data() }))
      );

    } catch (error) {
      console.error('Error loading results:', error);
      alert('Failed to load results');
      navigate('/learner');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Renders the outcome section below the score card.
   * Handles all four placement outcomes + lesson test outcomes.
   */
  const getActionSection = () => {
    if (!test || !attempt) return null;

    // ── Lesson / knowledge-check tests ──────────────────────────────────────
    if (attempt.testType === 'lessonTest') {
      if (attempt.passed) {
        return (
          <div className="action-section">
            <p className="action-message">
              🎉 Great work! You've completed this knowledge check.
            </p>
            <button onClick={() => navigate('/learner')} className="primary-btn">
              Back to Dashboard
            </button>
          </div>
        );
      }
      return (
        <div className="action-section">
          <p className="action-message">
            📚 Not quite there yet. Review the lesson and try again.
          </p>
          <div className="button-group">
            <a
              href={`${LEARN_APP_URL}/lesson/${test.lessonCode || test.newLessonUrl}`}
              className="primary-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              Go to Lesson
            </a>
            <button onClick={() => navigate('/learner')} className="secondary-btn">
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }

    // ── Placement tests ──────────────────────────────────────────────────────
    const levelId = attempt.levelId || test.levelId;

    if (!attempt.passed) {
      // FAIL — user has been auto-enrolled in this level's catalog
      const levelName = LEVEL_NAMES[levelId] || levelId;
      return (
        <div className="action-section outcome-fail">
          <div className="outcome-icon">📚</div>
          <h3 className="outcome-heading">You've been enrolled in {levelName}</h3>
          <p className="outcome-body">
            Don't worry — this is exactly where you should start! You've been automatically
            enrolled in the {levelName} course on Litvill Lessons. Work through the lessons
            and come back here to retake the test when you feel ready.
          </p>
          <div className="button-group">
            <a
              href={LEARN_APP_URL}
              className="primary-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              Go to Litvill Lessons →
            </a>
            <button onClick={() => navigate(`/test/${test.id}`)} className="secondary-btn">
              Retake Test
            </button>
          </div>
        </div>
      );
    }

    if (attempt.passed && levelId === 'level-4') {
      // PASS LEVEL 4 — enrolled in all advanced levels
      return (
        <div className="action-section outcome-pass-advanced">
          <div className="outcome-icon">🏆</div>
          <h3 className="outcome-heading">Outstanding — you've placed into advanced levels!</h3>
          <p className="outcome-body">
            You've passed all four placement tests. You've been enrolled in our advanced
            courses (Levels 5–10) on Litvill Lessons. Head over to get started!
          </p>
          <div className="button-group">
            <a
              href={LEARN_APP_URL}
              className="primary-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              Go to Litvill Lessons →
            </a>
            <button onClick={() => navigate('/learner')} className="secondary-btn">
              View Dashboard
            </button>
          </div>
        </div>
      );
    }

    if (attempt.passed && NEXT_LEVEL[levelId]) {
      if (levelComplete) {
        // ALL THREE tests in this level passed — next level unlocked
        const nextLevelName = LEVEL_NAMES[NEXT_LEVEL[levelId]] || NEXT_LEVEL[levelId];
        return (
          <div className="action-section outcome-pass-next">
            <div className="outcome-icon">⬆️</div>
            <h3 className="outcome-heading">Well done — {nextLevelName} is now unlocked!</h3>
            <p className="outcome-body">
              You've passed all three tests at this level. Head back to the dashboard to continue.
            </p>
            <div className="button-group">
              <button onClick={() => navigate('/learner')} className="primary-btn">
                Continue to Next Level →
              </button>
              <button onClick={() => navigate(`/test/${test.id}`)} className="secondary-btn">
                Retake This Test
              </button>
            </div>
          </div>
        );
      } else {
        // Passed this test but still more tests to do in this level
        return (
          <div className="action-section outcome-pass-next">
            <div className="outcome-icon">✅</div>
            <h3 className="outcome-heading">Test passed — keep going!</h3>
            <p className="outcome-body">
              Great result! Go back to the dashboard to complete the remaining tests at this level.
            </p>
            <div className="button-group">
              <button onClick={() => navigate('/learner')} className="primary-btn">
                Back to Dashboard →
              </button>
              <button onClick={() => navigate(`/test/${test.id}`)} className="secondary-btn">
                Retake This Test
              </button>
            </div>
          </div>
        );
      }
    }

    // Fallback
    return (
      <div className="action-section">
        <button onClick={() => navigate('/learner')} className="primary-btn">
          Back to Dashboard
        </button>
      </div>
    );
  };

  const getQuestionResult = (questionId) =>
    attempt?.results?.find(r => r.questionId === questionId);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
        <p>Loading results...</p>
      </div>
    );
  }

  if (!attempt || !test) {
    return (
      <div className="error-screen">
        <h2>Results not found</h2>
        <button onClick={() => navigate('/learner')}>Back to Dashboard</button>
      </div>
    );
  }

  const correctCount = attempt.results?.filter(r => r.isCorrect).length || 0;
  const totalQuestions = questions.length;
  const incorrectCount = totalQuestions - correctCount;

  return (
    <div className="test-results">
      <div className="results-header">
        <h1>{test.title || test.lessonTitle}</h1>
        <button onClick={() => navigate('/learner')} className="close-btn">✕</button>
      </div>

      <div className="results-container">
        {/* Score card */}
        <div className="score-card">
          <div className={`score-circle ${attempt.passed ? 'passed' : 'failed'}`}>
            <div className="score-percentage">{attempt.score}%</div>
            <div className="score-label">
              {attempt.passed ? '✓ Passed' : '✗ Not Passed'}
            </div>
          </div>

          <div className="score-details">
            <div className="score-stat">
              <div className="stat-icon correct">✓</div>
              <div className="stat-info">
                <div className="stat-value">{correctCount}</div>
                <div className="stat-label">Correct</div>
              </div>
            </div>
            <div className="score-stat">
              <div className="stat-icon incorrect">✗</div>
              <div className="stat-info">
                <div className="stat-value">{incorrectCount}</div>
                <div className="stat-label">Incorrect</div>
              </div>
            </div>
            <div className="score-stat">
              <div className="stat-icon total">=</div>
              <div className="stat-info">
                <div className="stat-value">{totalQuestions}</div>
                <div className="stat-label">Total</div>
              </div>
            </div>
          </div>

          <div className="pass-threshold-info">
            Pass threshold: {attempt.passThreshold ?? (test.settings?.passThreshold ?? 65)}%
          </div>
        </div>

        {/* Outcome / action section */}
        {getActionSection()}

        {/* Answer review (if enabled) */}
        {test.settings?.showCorrectAnswers && questions.length > 0 && (
          <div className="answers-section">
            <div className="section-header">
              <h2>Review Your Answers</h2>
              <button
                onClick={() => setShowAllAnswers(!showAllAnswers)}
                className="toggle-btn"
              >
                {showAllAnswers ? 'Hide All' : 'Show All'}
              </button>
            </div>

            <div className="questions-review">
              {questions.map((question, index) => {
                const result = getQuestionResult(question.id);
                return (
                  <div key={question.id} className="question-review-item">
                    <div
                      className="question-review-header"
                      onClick={() => {
                        const elem = document.getElementById(`question-${question.id}`);
                        if (elem) {
                          elem.style.display = elem.style.display === 'none' ? 'block' : 'none';
                        }
                      }}
                    >
                      <span className="question-review-number">Question {index + 1}</span>
                      <span className={`result-badge ${result?.isCorrect ? 'correct' : 'incorrect'}`}>
                        {result?.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                      </span>
                    </div>
                    <div
                      id={`question-${question.id}`}
                      className="question-review-content"
                      style={{ display: showAllAnswers ? 'block' : 'none' }}
                    >
                      <QuestionRenderer
                        question={question}
                        userAnswer={attempt.userAnswers?.[question.id]}
                        onAnswerChange={() => {}}
                        showFeedback={true}
                        showCorrectAnswers={true}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestResults;